/**
 * Shared date-range presets for finance list pages.
 *
 * Dates are formatted from LOCAL calendar parts, not via toISOString(). In any
 * timezone behind UTC, `new Date(y, m, 1).toISOString()` rolls back to the last
 * day of the previous month, which silently widened every range by a day.
 */

export type DatePreset = 'all' | 'week' | 'month' | 'last_month' | 'year' | 'custom';

export const DATE_PRESET_OPTIONS: { value: DatePreset; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'year', label: 'This Year' },
  { value: 'custom', label: 'Custom' },
];

/** Format a Date as YYYY-MM-DD using its local calendar parts. */
export function toLocalISODate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

export function getDatePreset(preset: DatePreset | string): { from: string; to: string } {
  const now = new Date();
  const today = toLocalISODate(now);

  switch (preset) {
    case 'week': {
      const d = new Date(now);
      d.setDate(d.getDate() - d.getDay());
      return { from: toLocalISODate(d), to: today };
    }
    case 'month':
      return {
        from: toLocalISODate(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: today,
      };
    case 'last_month':
      return {
        from: toLocalISODate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        // Day 0 of this month is the last day of the previous month.
        to: toLocalISODate(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    case 'year':
      return {
        from: toLocalISODate(new Date(now.getFullYear(), 0, 1)),
        to: today,
      };
    default:
      return { from: '', to: '' };
  }
}
